<template>
    <div v-if="ready">
        <form
            class="form-horizontal"
            enctype="multipart/form-data"
            @keydown="form.errors.clear($event.target.name)"
            autocomplete="off"
        >
            <div v-if="isBuyInvoice" class="row form-group" :class="{ 'has-danger' : form.errors.has('number_by_coach') }">
                <label class="col-md-3 col-sm-12 col-form-label">{{ uLang('app.invoice_number') }}</label>
                <div class="col-md-9 col-sm-12">
                    <input
                        type="text"
                        class="form-control"
                        v-model="form.number_by_coach"
                        :required="true"
                    >
                    <span
                        class="help-block form-control-feedback"
                        v-if="form.errors.has('number_by_coach')"
                        v-text="form.errors.get('number_by_coach')"
                    ></span>
                </div>
            </div>

            <div class="row form-group" :class="{ 'has-danger' : form.errors.has('rows.0.description') }">
                <label class="col-md-3 col-sm-12 col-form-label">{{ uLang('app.description') }}</label>
                <div class="col-md-9 col-sm-12">
                    <input
                            type="text"
                            class="form-control"
                            v-model="form.rows[0].description"
                            :required="true"
                    >
                    <span
                            class="help-block form-control-feedback"
                            v-if="form.errors.has('rows.0.description')"
                            v-text="form.errors.get('rows.0.description')"
                    ></span>
                </div>
            </div>

            <div class="row form-group" :class="{ 'has-danger' : form.errors.has('rows.0.price_no_vat') }">
                <label class="col-md-3 col-sm-12 col-form-label">{{ uLang('app.price_no_vat') }}</label>
                <div class="col-md-9 col-sm-12">
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <button
                            :title="uLang('app.credit_entire_invoice')"
                            class="btn btn-primary-outline"
                            type="button"
                            @click.prevent="setMaxCredit"
                        >
                        <span class="fa-stack">
                          <i class="fa fa-file fa-stack-2x"></i>
                          <i class="fa fa-backward fa-stack-1x fa-inverse"></i>
                        </span>
                        </button>
                      </div>
                      <input
                          type="text"
                          class="form-control"
                          v-model="form.rows[0].price_no_vat"
                          :required="true"
                      >
                    </div>
                    <span
                        class="help-block form-control-feedback"
                        v-if="form.errors.has('rows.0.price_no_vat')"
                        v-text="form.errors.get('rows.0.price_no_vat')"
                    ></span>
                </div>
            </div>

            <div class="row form-group" v-if="invoice">
                <label class="col-md-3 col-sm-12 col-form-label">{{ uLang('app.vat_percentage') }}</label>
                <div class="col-md-9 col-sm-12">
                    <input
                        type="number"
                        class="form-control"
                        :value="invoice.vat_percentage"
                        :readonly="true"
                    >
                </div>
            </div>

            <div class="row form-group" v-if="invoice">
                <label class="col-md-3 col-sm-12 col-form-label">{{ uLang('app.price_with_vat') }}</label>
                <div class="col-md-9 col-sm-12">
                    <input
                        type="text"
                        class="form-control"
                        :value="priceWithVat"
                        :readonly="true"
                    >
                    <span class="help-block form-control-feedback text-muted" :class="{'text-danger': !canBeCredited}" v-if="invoice">
                        {{ uLang('app.max_credit_is', { 'credit': !invoice.max_credit ? 0 : invoice.max_credit.toFixed(2) }) }} ({{ lang('app.includes_vat')}})
                    </span>
                </div>
            </div>
        </form>

        <div class="form-group" :class="{ 'has-danger' : form.errors.has('rows') }">
            <span
                class="help-block form-control-feedback"
                v-if="form.errors.has('rows')"
                v-text="form.errors.get('rows')"
            ></span><br>
            <input
                :disabled="form.busy || !canBeCredited"
                type="button"
                @click.prevent="store"
                class="btn btn-brand"
                :value="uLang('app.save')"
            />
        </div>
    </div>
</template>

<script>
    import Form from '~/Form/Form'
    import { FormMixin } from '~/mixins'
    import { Input as FormInput } from '~/Form/elements'
    import { SuccessAlert } from "~/Swals"

    export default {
        name: 'CreditInvoiceForm',

        mixins: [FormMixin],

        components: {
            FormInput,
        },

        props: {
            invoiceId: {
                type: Number,
                required: true,
            },
            type: {
                type: String,
                required: true,
                validator(value) {
                    return ['sales', 'buy'].includes(value)
                }
            }
        },

        data() {
            return {
                form: new Form({
                    rows: [
                        { description: '', price_no_vat: null },
                    ],
                    number_by_coach: null,
                }),
                entity: 'credit_invoice',
                invoice: null,
            }
        },

        watch: {
            invoiceId: {
                immediate: true,
                handler: async function(invoiceId) {
                    const { data } = await window.axios.get(route(`invoices.${this.type}.edit`, { invoice: this.invoiceId }));

                    this.invoice = data

                    // Set all invoice row's invoice_id to the current id.
                    for(let i=0; i < this.form.rows.length; i++) {
                        this.form.rows[i].invoice_id = data.id
                    }
                },
            },
        },

        created() {
            this.$on('afterStore', () => {
                this.form.reset();
                this.$emit('close');
            })
        },

        computed: {
            priceWithVat() {
                if (!this.invoice || !this.form.rows[0].price_no_vat) {
                    return 0
                }

                let price_with_vat = this.form.rows[0].price_no_vat * this.getUsableVatPercentage(this.invoice.vat_percentage)

                return price_with_vat.toFixed(2)
            },

            canBeCredited() {
                return this.invoice &&
                    Number.parseFloat(this.priceWithVat) <= Number.parseFloat(this.invoice.max_credit)
            },

            isBuyInvoice(){
                return this.invoice && !!this.invoice.buy_for
            },
        },

        methods: {
            setMaxCredit() {
                const price_no_vat = this.invoice.max_credit / this.getUsableVatPercentage(this.invoice.vat_percentage)

                this.form.rows[0].price_no_vat = price_no_vat.toFixed(2)
            },

            getUsableVatPercentage(vatPercentage) {
                return 1 + (vatPercentage / 100);
            }
        },
    }
</script>

<style lang="scss" scoped>
.btn {
    font-size: 11px;
}

.fa-stack {
    font-size: 11px;
}

.fa-backward::before {
    margin-left: -5px;
}
</style>